import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { STATUS_STYLES } from "@/lib/status";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { TrendingUp, Package, Truck, Users as UsersIcon } from "lucide-react";

export default function ReportsPage() {
  const [summary, setSummary] = useState(null);
  const [topUsers, setTopUsers] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [topSuppliers, setTopSuppliers] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, u, p, sup] = await Promise.all([
        api.get("/reports/summary"),
        api.get("/reports/top-users"),
        api.get("/reports/top-products"),
        api.get("/reports/top-suppliers"),
      ]);
      setSummary(s.data);
      setTopUsers(u.data);
      setTopProducts(p.data);
      setTopSuppliers(sup.data);
    })();
  }, []);

  if (!summary) return <div className="text-slate-400">Yükleniyor...</div>;

  const statusData = Object.entries(summary.by_status).map(([k, v]) => ({ name: k, value: v }));
  const statusColors = {
    "Oluşturuldu": "#94a3b8",
    "WhatsApp'a Aktarıldı": "#22c55e",
    "Beklemede": "#f59e0b",
    "Sipariş Verildi": "#3b82f6",
    "Kısmi Geldi": "#6366f1",
    "Tam Geldi": "#10b981",
    "Kapatıldı": "#8b5cf6",
    "İptal": "#f43f5e",
  };

  return (
    <div className="fade-up">
      <div className="mb-6">
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Raporlar</h1>
        <p className="text-sm text-slate-500 mt-1">Talep istatistikleri ve performans göstergeleri</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <Stat label="Toplam Talep" value={summary.total} icon={TrendingUp} color="text-slate-700" />
        <Stat label="Açık Talep" value={summary.open} icon={Package} color="text-amber-700" />
        <Stat label="Tamamlanan" value={summary.completed} icon={Truck} color="text-emerald-700" />
        <Stat label="İptal" value={summary.by_status["İptal"]} icon={UsersIcon} color="text-rose-700" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Status distribution */}
        <Card title="Duruma Göre Dağılım" testId="status-distribution">
          <div className="space-y-2">
            {statusData.map((s) => (
              <div key={s.name} className="flex items-center gap-3 text-sm" data-testid={`status-stat-${s.name}`}>
                <div className="w-44 sm:w-52 truncate">
                  <span className={`badge-status ${STATUS_STYLES[s.name]}`}>{s.name}</span>
                </div>
                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full" style={{ width: `${summary.total ? (s.value/summary.total*100) : 0}%`, background: statusColors[s.name] }} />
                </div>
                <div className="w-10 text-right font-medium">{s.value}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="En Çok Sipariş Veren Kullanıcılar" testId="top-users">
          <ChartList data={topUsers.map((u) => ({ name: u.name, value: u.count }))} color="#0F172A" />
        </Card>

        <Card title="En Çok Sipariş Edilen Ürünler" testId="top-products">
          <ChartList data={topProducts.map((p) => ({ name: `${p.code} · ${p.name}`, value: p.count }))} color="#10B981" />
        </Card>

        <Card title="En Çok Çalışılan Tedarikçiler" testId="top-suppliers">
          <ChartList data={topSuppliers.map((s) => ({ name: s.name, value: s.count }))} color="#3B82F6" />
        </Card>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className={`flex items-center gap-2 ${color}`}>
        <Icon className="h-4 w-4" />
        <span className="text-xs font-bold uppercase tracking-wider">{label}</span>
      </div>
      <div className="font-display text-3xl font-semibold mt-1.5">{value || 0}</div>
    </div>
  );
}

function Card({ title, children, testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5">
      <div className="text-sm font-semibold text-slate-900 mb-3">{title}</div>
      {children}
    </div>
  );
}

function ChartList({ data, color }) {
  if (!data || data.length === 0) {
    return <div className="text-sm text-slate-400 text-center py-6">Henüz veri yok</div>;
  }
  return (
    <div className="h-56">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ left: 0, right: 16 }}>
          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
          <YAxis dataKey="name" type="category" width={140} tick={{ fontSize: 11, fill: "#334155" }} axisLine={false} tickLine={false} />
          <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
          <Bar dataKey="value" fill={color} radius={[0, 4, 4, 0]}>
            {data.map((_, i) => <Cell key={i} fill={color} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
